import React, { forwardRef } from "react";
import { EntityCard, EntityCardRef, EntityCardProps, BaseEntity } from "components/Entities/EntityCardBase";
import { Solution } from "util/constants";
import InputTextArea from "ui/InputTextArea";
import PostKindChip from "components/Posts/display/PostKindChip";
import PostMediaPreview from "components/Posts/display/PostMediaPreview";
import PostReferenceLink from "components/Posts/display/PostReferenceLink";
import ContextCommentsSection from "components/Posts/sections/ContextCommentsSection";

interface SolutionCardRefactoredProps extends Omit<EntityCardProps<Solution>, "entity" | "entityType" | "renderTags" | "renderAdditionalContent" | "renderEditFields" | "renderActions" | "renderPrimaryContent"> {
  solution: Solution;
  onTitleChange?: (value: string) => void;
  onReferenceLinkChange?: (value: string) => void;
  onPrivacyChange?: (value: string) => void;
  titleValidationMessage?: string;
  referenceLinkValidationMessage?: string;
  onCardClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Refactored SolutionCard using the generic EntityCard component
 */
const SolutionCardRefactored = forwardRef<EntityCardRef, SolutionCardRefactoredProps>(
  (props, ref) => {
    const { 
      solution, 
      onTitleChange,
      onReferenceLinkChange,
      onPrivacyChange,
      titleValidationMessage,
      referenceLinkValidationMessage,
      onCardClick, 
      ...restProps 
    } = props;
    
    // Default card click handler
    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const element = event.target as HTMLElement;
      if (element.tagName.toLowerCase() === 'a') return;    
      if (element.classList.contains('read-more')) return;
      if (!props.isEntityProfilePage) {
        event.stopPropagation();
        // Navigate will be handled by EntityCard
      } else {
        event.preventDefault();            
        if (element.tagName.toLowerCase() === 'img' || 
            element.tagName.toLowerCase() === 'svg' || 
            element.tagName.toLowerCase() === 'path' || 
            element.classList.contains('no-metadata-link')) {
          event.stopPropagation();
          if (solution.url) {
            window.open(solution.url, '_blank', 'noopener,noreferrer');
          }
        }
      }
      
      if (onCardClick) {
        onCardClick(event);
      }
    };
    
    // Helper function to check if title should be hidden
    const shouldHideTitle = (title?: string): boolean => {
      return !title || title.trim() === '' || title.trim() === '[no_title]';
    };

    // Render solution-specific tags
    const renderTags = (_entity: BaseEntity) => {
      return <PostKindChip kind="solution" className="bg-eoe-entity-solution text-white" />;
    };

    // Render solution-specific additional content (reference link in view mode)
    const renderAdditionalContent = (entity: BaseEntity) => {
      const solution = entity as Solution;

      return (
        <>
          {solution.url && solution.url.trim() !== '' ? (
            <div className="my-1">
              <PostReferenceLink url={solution.url} />
            </div>
          ) : null}
          <ContextCommentsSection
            postId={solution.id}
            kind="principle"
            parentPrivacy={solution.properties?.privacy}
            editable={Boolean(props.editMode)}
          />
        </>
      );
    };

    // Render solution-specific edit fields
    const renderEditFields = (entity: BaseEntity) => {
      const solution = entity as Solution;
      
      return (
        <>
          {/* Title field */}
          <div className="form-control my-1 md:col-span-3">
            <InputTextArea
              id="solution-title"
              name="solution-title"
              labelText="Title"
              fieldRequired={false}
              value={solution.title}
              onChange={(e) => onTitleChange?.(e.target.value)}
              autoComplete="solution-title"
              maxLength={300}
              error={titleValidationMessage}
              validationState={titleValidationMessage ? 'invalid' : !titleValidationMessage && solution.title && solution.title.length > 0 ? 'valid' : 'none'}
            />
          </div>
          
          {/* Reference Link field */}
          <div className="my-2 md:col-span-3">
            <InputTextArea
              id="reference-link"
              name="reference-link"
              labelText="Reference URL"
              value={solution.url || ""}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onReferenceLinkChange?.(e.target.value)}
              autoComplete="url"
              spellCheck={false}
              error={referenceLinkValidationMessage}
              validationState={referenceLinkValidationMessage ? 'invalid' : 'none'}
            />
          </div>
        </>
      );
    };

    // Custom render actions for solution thumbnails/embeds
    const renderActions = (entity: BaseEntity) => {
      const solution = entity as Solution;
      
      if (solution.url && solution.url.trim() !== "") {
        return (
          <PostMediaPreview
            url={solution.url}
            isProfilePage={props.isEntityProfilePage || false}
            onCardClick={handleCardClick}
          />
        );
      }
      
      return null;
    };

    // Map Solution fields to BaseEntity
    const mappedEntity: BaseEntity = {
      ...solution,
      title: solution.title,
      body: solution.body
    };

    return (
      <EntityCard
        ref={ref}
        entity={mappedEntity}
        entityType="solution"
        onPrivacyChange={onPrivacyChange}
        renderTags={renderTags}
        renderAdditionalContent={renderAdditionalContent}
        renderEditFields={renderEditFields}
        renderActions={renderActions}
        hideTitle={shouldHideTitle(solution.title)}
        descriptionPlaceholder="Body"
        canEditPrivacy={true}
        canEditTitle={false} // We handle title editing in renderEditFields
        {...restProps}
      />
    );
  }
);

SolutionCardRefactored.displayName = "SolutionCardRefactored";

export default SolutionCardRefactored;
